import React from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  Popover,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import { positionAlias } from "../constant/positionList";
import { jerseyNumberOf } from "./PlayerMetaLine";

// ─── Shared player-form primitives ────────────────────────────────────────
// Used by the match-lineup "Add New Player" modal and by the admin duplicate
// approval screen, which renders the very same layout read-only.

export const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "13px",
    backgroundColor: "var(--surface-2)",
    "& fieldset": { borderColor: "var(--border-1)" },
    "&:hover fieldset": { borderColor: "var(--text-2)" },
  },
};

/** Tighter field metrics for the read-only/review rendering. */
export const compactFieldSx = {
  ...fieldSx,
  "& .MuiOutlinedInput-root": {
    ...fieldSx["& .MuiOutlinedInput-root"],
    fontSize: "12px",
  },
  "& .MuiOutlinedInput-input": { py: "5px" },
};

export const FieldLabel = ({ children, required, compact }) => (
  <Typography
    sx={{
      fontSize: compact ? "11px" : "12px",
      fontWeight: 600,
      color: "var(--text-2)",
      mb: compact ? 0.25 : 0.5,
    }}
  >
    {children}
    {required && <span style={{ color: "#EF4444", marginLeft: 2 }}>*</span>}
  </Typography>
);

export const CustomSelect = ({ label, required, value, onChange, options, placeholder, readOnly, compact }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const selectedLabel = options.find((o) => o.value === value)?.label || "";

  return (
    <Stack sx={{ flex: 1, minWidth: 0 }}>
      <FieldLabel required={required} compact={compact}>{label}</FieldLabel>
      <Box
        onClick={readOnly ? undefined : (e) => setAnchorEl(e.currentTarget)}
        sx={{
          height: compact ? 30 : 38,
          borderRadius: "8px",
          border: "1px solid var(--border-1)",
          backgroundColor: "var(--surface-2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          cursor: readOnly ? "default" : "pointer",
          "&:hover": { borderColor: readOnly ? "var(--border-1)" : "var(--text-2)" },
        }}
      >
        <Typography
          sx={{
            fontSize: compact ? "12px" : "13px",
            color: selectedLabel ? "var(--text-1)" : "var(--text-2)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {selectedLabel || placeholder || "Select"}
        </Typography>
        {!readOnly && (
          <Typography sx={{ fontSize: "12px", color: "var(--text-2)", ml: 0.5 }}>▾</Typography>
        )}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "10px",
              border: "1px solid var(--border-1)",
              boxShadow: "var(--shadow-1)",
              mt: 0.5,
              maxHeight: 240,
              minWidth: anchorEl?.offsetWidth || 180,
              overflow: "auto",
            },
          },
        }}
      >
        <List dense disablePadding>
          {options.map((opt) => (
            <ListItemButton
              key={opt.value}
              selected={opt.value === value}
              onClick={() => {
                onChange(opt.value);
                setAnchorEl(null);
              }}
              sx={{
                py: 0.75,
                px: 1.5,
                "&.Mui-selected": {
                  backgroundColor: "rgba(232, 108, 0, 0.1)",
                  "&:hover": { backgroundColor: "rgba(232, 108, 0, 0.15)" },
                },
              }}
            >
              <ListItemText
                primary={opt.label}
                primaryTypographyProps={{
                  fontSize: "13px",
                  fontWeight: opt.value === value ? 600 : 400,
                  color: opt.value === value ? "rgba(232, 108, 0, 1)" : "var(--text-1)",
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Popover>
    </Stack>
  );
};

export const CustomDateField = ({ label, required, value, onChange, readOnly, compact }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [viewYear, setViewYear] = React.useState(() => {
    if (value) return parseInt(value.split("-")[0]);
    return new Date().getFullYear();
  });
  const [viewMonth, setViewMonth] = React.useState(() => {
    if (value) return parseInt(value.split("-")[1]) - 1;
    return new Date().getMonth();
  });
  const open = Boolean(anchorEl);

  const displayVal = value
    ? new Date(value).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
    : "";

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const monthName = new Date(viewYear, viewMonth).toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const selectedDay = value ? parseInt(value.split("-")[2]) : null;
  const selectedMonth = value ? parseInt(value.split("-")[1]) - 1 : null;
  const selectedYear = value ? parseInt(value.split("-")[0]) : null;

  const pickDay = (d) => {
    const m = String(viewMonth + 1).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    onChange(`${viewYear}-${m}-${dd}`);
    setAnchorEl(null);
  };

  return (
    <Stack sx={{ flex: 1, minWidth: 0 }}>
      <FieldLabel required={required} compact={compact}>{label}</FieldLabel>
      <Box
        onClick={readOnly ? undefined : (e) => setAnchorEl(e.currentTarget)}
        sx={{
          height: compact ? 30 : 38,
          borderRadius: "8px",
          border: "1px solid var(--border-1)",
          backgroundColor: "var(--surface-2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          cursor: readOnly ? "default" : "pointer",
          "&:hover": { borderColor: readOnly ? "var(--border-1)" : "var(--text-2)" },
        }}
      >
        <Typography
          sx={{
            fontSize: compact ? "12px" : "13px",
            color: displayVal ? "var(--text-1)" : "var(--text-2)",
          }}
        >
          {displayVal || "dd/mm/yyyy"}
        </Typography>
        {!readOnly && (
          <Typography sx={{ fontSize: "14px", color: "var(--text-2)" }}>📅</Typography>
        )}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              border: "1px solid var(--border-1)",
              boxShadow: "var(--shadow-1)",
              mt: 0.5,
              p: 1.5,
              width: 280,
            },
          },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <IconButton size="small" onClick={() => { if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); } else setViewMonth((m) => m - 1); }}>
            <Typography sx={{ fontSize: "16px" }}>‹</Typography>
          </IconButton>
          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "var(--text-1)" }}>{monthName}</Typography>
          <IconButton size="small" onClick={() => { if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); } else setViewMonth((m) => m + 1); }}>
            <Typography sx={{ fontSize: "16px" }}>›</Typography>
          </IconButton>
        </Stack>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px", textAlign: "center" }}>
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
            <Typography key={d} sx={{ fontSize: "10px", fontWeight: 600, color: "var(--text-2)", py: 0.5 }}>{d}</Typography>
          ))}
          {days.map((d, i) => (
            <Box
              key={i}
              onClick={d ? () => pickDay(d) : undefined}
              sx={{
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "8px",
                cursor: d ? "pointer" : "default",
                fontSize: "12px",
                fontWeight: d === selectedDay && viewMonth === selectedMonth && viewYear === selectedYear ? 700 : 400,
                color: d === selectedDay && viewMonth === selectedMonth && viewYear === selectedYear ? "#fff" : d ? "var(--text-1)" : "transparent",
                backgroundColor: d === selectedDay && viewMonth === selectedMonth && viewYear === selectedYear ? "rgba(232, 108, 0, 1)" : "transparent",
                "&:hover": d ? { backgroundColor: "var(--surface-2)" } : {},
              }}
            >
              {d || ""}
            </Box>
          ))}
        </Box>
      </Popover>
    </Stack>
  );
};

export const LabeledField = ({ label, required, compact, children }) => (
  <Stack sx={{ flex: 1, minWidth: 0 }}>
    <FieldLabel required={required} compact={compact}>{label}</FieldLabel>
    {children}
  </Stack>
);

// ─── Position Constants ───
export const POSITIONS = [
  "GK","CB","LB","RB","LWB","RWB","CDM","CM","LM","RM",
  "CAM","LW","RW","LF","RF","CF","ST",
];

export const EMPTY_PLAYER_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  countryCode: "",
  position: "",
  dob: "",
  height: "",
  weight: "",
  preferredFoot: "",
  jerseyNumber: "",
  isDummy: false,
};

/** API `user_data` / `new_player_created` payload → this form's value shape. */
export const playerFormFromUserData = (u = {}) => ({
  ...EMPTY_PLAYER_FORM,
  firstName: u.first_name ?? "",
  lastName: u.last_name ?? "",
  email: u.email ?? "",
  phone: u.mobile ?? u.mobile_number ?? "",
  countryCode: u.country_code ?? "",
  position: positionAlias(u.preferred_position),
  dob: u.dob ?? "",
  height: u.height ?? "",
  weight: u.weight ?? "",
  preferredFoot: u.preferred_foot || "",
  jerseyNumber: jerseyNumberOf(u) ?? "",
  isDummy: u.category === 0,
});

/**
 * The player detail form.
 *  - `readOnly` locks every control (admin duplicate-approval review).
 *  - `compact` tightens the metrics for a view-only rendering.
 *  - `showDummy` hides the Dummy Player checkbox where it isn't actionable.
 *
 * Rows are emitted as a bare fragment unless `compact`, so the create modal
 * keeps getting its spacing from the parent DialogContent.
 */
export default function PlayerProfileForm({
  value,
  onChange,
  readOnly = false,
  compact = false,
  showDummy = true,
}) {
  const set = (patch) => onChange && onChange((f) => ({ ...f, ...patch }));
  const ro = readOnly ? { readOnly: true } : undefined;
  const sx = compact ? compactFieldSx : fieldSx;
  const rowGap = compact ? 1 : 1.5;
  const field = { size: "small", fullWidth: true, InputProps: ro, sx };
  const Wrapper = compact ? Stack : React.Fragment;
  const wrapperProps = compact ? { gap: 1 } : {};

  return (
    <Wrapper {...wrapperProps}>
      <Stack direction="row" gap={rowGap}>
        <LabeledField label="First Name" required compact={compact}>
          <TextField {...field} placeholder="First Name" value={value.firstName} onChange={(e) => set({ firstName: e.target.value })} />
        </LabeledField>
        <LabeledField label="Last Name" compact={compact}>
          <TextField {...field} placeholder="Last Name" value={value.lastName} onChange={(e) => set({ lastName: e.target.value })} />
        </LabeledField>
      </Stack>

      <Stack direction="row" gap={rowGap}>
        <LabeledField label="Email Id" compact={compact}>
          <TextField {...field} type="email" placeholder="Email Id" value={value.email} onChange={(e) => set({ email: e.target.value })} />
        </LabeledField>
        <LabeledField label="Phone Number" compact={compact}>
          <TextField {...field} placeholder="Phone Number" value={value.phone} onChange={(e) => set({ phone: e.target.value })} />
        </LabeledField>
      </Stack>

      <Stack direction="row" gap={rowGap}>
        <CustomSelect
          label="Preferred Position"
          value={value.position}
          onChange={(v) => set({ position: v })}
          placeholder="Select"
          readOnly={readOnly}
          compact={compact}
          options={[{ value: "", label: "Select" }, ...POSITIONS.map((p) => ({ value: p, label: p }))]}
        />
        <CustomDateField
          label="Date of Birth"
          value={value.dob}
          onChange={(v) => set({ dob: v })}
          readOnly={readOnly}
          compact={compact}
        />
      </Stack>

      <Stack direction="row" gap={rowGap}>
        <LabeledField label="Height (in cm)" compact={compact}>
          <TextField {...field} type="number" placeholder="Optional" value={value.height} onChange={(e) => set({ height: e.target.value })} />
        </LabeledField>
        <LabeledField label="Weight (in kg)" compact={compact}>
          <TextField {...field} type="number" placeholder="Optional" value={value.weight} onChange={(e) => set({ weight: e.target.value })} />
        </LabeledField>
      </Stack>

      <Stack direction="row" gap={rowGap}>
        <CustomSelect
          label="Preferred Foot"
          value={value.preferredFoot}
          onChange={(v) => set({ preferredFoot: v })}
          placeholder="Select"
          readOnly={readOnly}
          compact={compact}
          options={[
            { value: "", label: "Select" },
            { value: "Right", label: "Right" },
            { value: "Left", label: "Left" },
            { value: "Both", label: "Both" },
          ]}
        />
        <LabeledField label="Jersey Number" compact={compact}>
          <TextField {...field} type="number" placeholder="e.g. 10" value={value.jerseyNumber} onChange={(e) => set({ jerseyNumber: e.target.value })} />
        </LabeledField>
      </Stack>

      {showDummy && (
        <FormControlLabel
          control={
            <Checkbox
              checked={!!value.isDummy}
              onChange={(e) => set({ isDummy: e.target.checked })}
              disabled={readOnly}
              size="small"
              sx={{
                color: "var(--text-2)",
                "&.Mui-checked": {
                  color: "rgba(232, 108, 0, 1)",
                },
              }}
            />
          }
          label={
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                color: "var(--text-1)",
              }}
            >
              Dummy Player
            </Typography>
          }
        />
      )}
    </Wrapper>
  );
}
